/**
 * Admin Route Guard Component (Story 7A.2)
 *
 * Protects admin routes by checking authentication and admin claim.
 * Redirects unauthorized users to /admin/login.
 *
 * FR59: Admin Panel Access
 *
 * @module components/admin/AdminRoute
 */

import { Navigate } from 'react-router-dom';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { LoadingIcon } from '../icons';

interface AdminRouteProps {
  /** Child components to render if authorized */
  children: React.ReactNode;
}

/**
 * Route guard that requires admin authentication
 *
 * Checks:
 * 1. User is authenticated (Firebase Auth)
 * 2. User has isAdmin: true custom claim
 *
 * @example
 * ```tsx
 * <Route
 *   path="/admin/dashboard"
 *   element={
 *     <AdminRoute>
 *       <AdminDashboardPage />
 *     </AdminRoute>
 *   }
 * />
 * ```
 */
export function AdminRoute({ children }: AdminRouteProps): JSX.Element {
  const { user, isAdmin, loading } = useAdminAuth();

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <LoadingIcon className="animate-spin text-primary-600" size={32} />
      </div>
    );
  }

  // Redirect to login if not authenticated or not admin
  if (!user || !isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  // Render protected content
  return <>{children}</>;
}
